
import { LeadStatus } from "@/types";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Filter,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LeadCountsType {
  all: number;
  queued: number;
  "in-progress": number;
  completed: number;
  reactivated: number;
  "not-interested": number;
}

interface LeadFiltersProps {
  activeTab: LeadStatus | "all";
  handleTabChange: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  selectedLeads: Set<string>;
  setShowDeleteDialog: (show: boolean) => void;
  handleSelectAllChange: (select: boolean) => void;
  leadCounts: LeadCountsType;
}

const LeadFilters = ({
  activeTab,
  handleTabChange,
  searchQuery,
  setSearchQuery,
  selectedLeads,
  setShowDeleteDialog,
  handleSelectAllChange,
  leadCounts,
}: LeadFiltersProps) => {
  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full md:w-auto md:min-w-[320px]">
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-3 pr-9"
          />
          {searchQuery && (
            <button 
              className="absolute right-3 top-[9px] text-gray-400 hover:text-gray-600"
              onClick={clearSearch}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-auto">
          {selectedLeads.size > 0 && (
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center gap-1"
            >
              <span>Delete {selectedLeads.size} selected</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto">
                <Filter className="h-4 w-4 mr-2" />
                <span>Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleSelectAllChange(true)}>
                Select All Visible
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSelectAllChange(false)}>
                Deselect All
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={selectedLeads.size === 0}
                onClick={() => selectedLeads.size > 0}
              >
                Start Campaign
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={selectedLeads.size === 0}
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600"
              >
                Delete Selected
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-4">
          <TabsTrigger value="all">
            All 
            <Badge variant="outline" className="ml-1">{leadCounts.all}</Badge>
          </TabsTrigger>
          <TabsTrigger value="queued">
            Queued
            <Badge variant="outline" className="ml-1">{leadCounts.queued}</Badge>
          </TabsTrigger>
          <TabsTrigger value="in-progress">
            In Progress
            <Badge variant="outline" className="ml-1">{leadCounts["in-progress"]}</Badge>
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed
            <Badge variant="outline" className="ml-1">{leadCounts.completed}</Badge>
          </TabsTrigger>
          <TabsTrigger value="reactivated">
            Reactivated
            <Badge variant="outline" className="ml-1">{leadCounts.reactivated}</Badge>
          </TabsTrigger>
          <TabsTrigger value="not-interested">
            Not Interested
            <Badge variant="outline" className="ml-1">{leadCounts["not-interested"]}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
};

export default LeadFilters;
